import { memo, useMemo, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  SortDescriptor,
} from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { calculateFOBSettlement, formatMoneyUSD, formatNumber } from '../../utils/functions';
import { UNIFIED_TABLE_CLASSNAMES, TableExportRef } from '../../utils/table-styles';
import { exportTableToExcel } from '../../utils/export-to-excel';

interface ClientSettlementData {
  client: string;
  count: number;
  totalFob: number;
  totalKilos: number;
}

export interface ClientAggregatedRow {
  key: string | null;
  total_fob: number;
  total_kilos: number;
  total_extra_revenue_usd: number;
  settlement_count?: number;
}

const ClientSettlementsTable = forwardRef<TableExportRef, {
  settlements?: Settlement[];
  aggregated?: ClientAggregatedRow[];
  boxSize?: number;
  kgOrBox?: 'kg' | 'box';
  showWithRevenues?: boolean;
}>(({
  settlements,
  aggregated,
  boxSize = 5,
  kgOrBox = 'box',
  showWithRevenues = false,
}, ref) => {
  const { t } = useTranslation('settlements');
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'totalFob',
    direction: 'descending',
  });

  const clientDataArray = useMemo(() => {
    // Prefer pre-aggregated data from the server if provided; the server
    // already applied filters and summed by receiver. Otherwise fall back to
    // the legacy client-side reduce over raw settlements.
    if (aggregated) {
      return aggregated.map((row) => ({
        client: row.key ?? t('Sin Cliente'),
        count: row.settlement_count ?? 0,
        totalFob:
          row.total_fob +
          (showWithRevenues ? row.total_extra_revenue_usd : 0),
        totalKilos: row.total_kilos,
      }));
    }
    const rawSettlements = settlements ?? [];
    const clientData = rawSettlements.reduce((acc, settlement) => {
      const client = settlement.receiver;
      if (!acc[client]) {
        acc[client] = { client, count: 0, totalFob: 0, totalKilos: 0 };
      }
      acc[client].count += 1;
      acc[client].totalFob += settlement.settlement_details.reduce(
        (sum, d) => sum + (d.usd_total_fob || 0),
        0
      );
      acc[client].totalKilos += settlement.settlement_details.reduce(
        (sum, d) => sum + (d.kilos || 0),
        0
      );
      return acc;
    }, {} as Record<string, ClientSettlementData>);
    return Object.values(clientData);
  }, [settlements, aggregated, showWithRevenues, t]);

  const grandTotalKilos = useMemo(
    () => clientDataArray.reduce((sum, c) => sum + c.totalKilos, 0),
    [clientDataArray]
  );
  const grandTotalFob = useMemo(
    () => clientDataArray.reduce((sum, c) => sum + c.totalFob, 0),
    [clientDataArray]
  );

  const multiplier = kgOrBox === 'kg' ? 1 : boxSize;
  const volumeLabel = kgOrBox === 'kg' ? 'KG' : `CEQ ${boxSize}KG`;
  const fobUnitLabel = kgOrBox === 'kg' ? 'FOB/KG' : `FOB/CEQ ${boxSize}KG`;

  const getVolume = (kilos: number) => (kgOrBox === 'kg' ? kilos : kilos / boxSize);
  const getFobPct = (fob: number) => (grandTotalFob > 0 ? (fob / grandTotalFob) * 100 : 0);
  const getVolPct = (kilos: number) => (grandTotalKilos > 0 ? (kilos / grandTotalKilos) * 100 : 0);
  const getFobPerUnit = (fob: number, kilos: number) =>
    kilos > 0 ? (fob / kilos) * multiplier : 0;

  const getSortValue = useCallback(
    (item: ClientSettlementData, col: string): number => {
      switch (col) {
        case 'client': return 0; // handled as string sort
        case 'count': return item.count;
        case 'totalFob': return item.totalFob;
        case 'fobPct': return getFobPct(item.totalFob);
        case 'volume': return getVolume(item.totalKilos);
        case 'volPct': return getVolPct(item.totalKilos);
        case 'fobPerUnit': return getFobPerUnit(item.totalFob, item.totalKilos);
        default: return 0;
      }
    },
    [grandTotalFob, grandTotalKilos, multiplier, kgOrBox, boxSize]
  );

  const sortedData = useMemo(() => {
    const col = sortDescriptor.column as string;
    const dir = sortDescriptor.direction === 'ascending' ? 1 : -1;

    return [...clientDataArray].sort((a, b) => {
      if (col === 'client') return dir * a.client.localeCompare(b.client);
      return dir * (getSortValue(a, col) - getSortValue(b, col));
    });
  }, [clientDataArray, sortDescriptor, getSortValue]);

  // Prepend a TOTAL row, always shown at the top regardless of sort.
  const grandTotalCount = useMemo(
    () => clientDataArray.reduce((s, c) => s + c.count, 0),
    [clientDataArray]
  );
  const dataWithTotal = useMemo(() => {
    if (sortedData.length === 0) return [] as (ClientSettlementData & { isTotal?: boolean })[];
    const totalRow: ClientSettlementData & { isTotal?: boolean } = {
      client: t('TOTAL'),
      count: grandTotalCount,
      totalFob: grandTotalFob,
      totalKilos: grandTotalKilos,
      isTotal: true,
    };
    return [totalRow, ...sortedData];
  }, [sortedData, grandTotalCount, grandTotalFob, grandTotalKilos, t]);

  const handleExport = useCallback(() => {
    exportTableToExcel({
      fileName: 'distribucion-por-cliente',
      sheetName: 'Clientes',
      headers: [t('CLIENTE'), t('LIQ.'), t('FOB TOTAL'), t('% FOB'), volumeLabel, t('% VOL.'), fobUnitLabel],
      rows: sortedData.map((item) => [
        item.client,
        item.count,
        Math.round(item.totalFob),
        grandTotalFob > 0 ? parseFloat(getFobPct(item.totalFob).toFixed(1)) : 0,
        Math.round(getVolume(item.totalKilos)),
        grandTotalKilos > 0 ? parseFloat(getVolPct(item.totalKilos).toFixed(1)) : 0,
        item.totalKilos > 0 ? parseFloat(getFobPerUnit(item.totalFob, item.totalKilos).toFixed(2)) : 0,
      ]),
    });
  }, [sortedData, grandTotalFob, grandTotalKilos, volumeLabel, fobUnitLabel, t]);

  useImperativeHandle(ref, () => ({ exportToExcel: handleExport }), [handleExport]);

  return (
    <Table
      aria-label={t('Distribución por Cliente')}
      sortDescriptor={sortDescriptor}
      onSortChange={setSortDescriptor}
      isHeaderSticky
      classNames={{
        ...UNIFIED_TABLE_CLASSNAMES,
        base: 'max-h-[600px] overflow-auto',
      }}
    >
      <TableHeader>
        <TableColumn key='client' allowsSorting>{t('CLIENTE')}</TableColumn>
        <TableColumn key='count' allowsSorting align='center'>{t('LIQ.')}</TableColumn>
        <TableColumn key='totalFob' allowsSorting align='end'>{t('FOB TOTAL')}</TableColumn>
        <TableColumn key='fobPct' allowsSorting align='end'>{t('% FOB')}</TableColumn>
        <TableColumn key='volume' allowsSorting align='end'>{volumeLabel}</TableColumn>
        <TableColumn key='volPct' allowsSorting align='end'>{t('% VOL.')}</TableColumn>
        <TableColumn key='fobPerUnit' allowsSorting align='end'>{fobUnitLabel}</TableColumn>
      </TableHeader>
      <TableBody items={dataWithTotal}>
        {(item: ClientSettlementData & { isTotal?: boolean }) => (
          <TableRow
            key={item.isTotal ? '__total__' : item.client}
            className={
              item.isTotal
                ? 'bg-default-100 dark:bg-default-50 font-semibold border-b-2 border-default-200 dark:border-default-100'
                : ''
            }
          >
            <TableCell>
              <span className={item.isTotal ? 'font-bold' : 'font-medium'}>
                {item.client}
              </span>
            </TableCell>
            <TableCell>
              <span
                className={
                  item.isTotal ? 'font-semibold' : 'text-default-500'
                }
              >
                {item.count}
              </span>
            </TableCell>
            <TableCell>
              <span className={item.isTotal ? 'font-semibold' : ''}>
                {formatMoneyUSD(item.totalFob, { decimals: 0 })}
              </span>
            </TableCell>
            <TableCell>
              {item.isTotal ? (
                <span className='font-semibold flex justify-end'>—</span>
              ) : grandTotalFob > 0 ? (
                <div className='flex items-center justify-end gap-2'>
                  <div className='w-12 h-1.5 rounded-full bg-default-200 overflow-hidden'>
                    <div
                      className='h-full rounded-full bg-primary-400'
                      style={{ width: `${Math.min(getFobPct(item.totalFob), 100)}%` }}
                    />
                  </div>
                  <span className='text-default-500 min-w-[3rem] text-right'>
                    {getFobPct(item.totalFob).toFixed(1)}%
                  </span>
                </div>
              ) : '—'}
            </TableCell>
            <TableCell>
              <span className={item.isTotal ? 'font-semibold' : ''}>
                {formatNumber(getVolume(item.totalKilos), { decimals: 0 })}
              </span>
            </TableCell>
            <TableCell>
              {item.isTotal ? (
                <span className='font-semibold flex justify-end'>—</span>
              ) : grandTotalKilos > 0 ? (
                <div className='flex items-center justify-end gap-2'>
                  <div className='w-12 h-1.5 rounded-full bg-default-200 overflow-hidden'>
                    <div
                      className='h-full rounded-full bg-primary-400'
                      style={{ width: `${Math.min(getVolPct(item.totalKilos), 100)}%` }}
                    />
                  </div>
                  <span className='text-default-500 min-w-[3rem] text-right'>
                    {getVolPct(item.totalKilos).toFixed(1)}%
                  </span>
                </div>
              ) : '—'}
            </TableCell>
            <TableCell>
              <span className={item.isTotal ? 'font-bold' : 'font-semibold'}>
                {item.totalKilos > 0
                  ? formatMoneyUSD(
                      getFobPerUnit(item.totalFob, item.totalKilos),
                      { decimals: 2 }
                    )
                  : '—'}
              </span>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
});

export default memo(ClientSettlementsTable);
